"use client"
import { React, useState } from "react"
import { Modal, Button, Badge, Card, Row, Col, Spinner } from "react-bootstrap"
import { Calendar, Users, MessageSquare, ThumbsUp, MapPin, Tag, CheckCircle, AlertCircle, Lightbulb, ChevronRight } from 'lucide-react'
import { PostCampaign } from "./PostCampaign"
import { connect } from "react-redux"
import { useFetchCampaignsByProblem } from "../hooks/DataFetching/useFetchCampaignsByProblem"
import CampaignModal from "./CampaignModal" // Import the CampaignModal component
import "../styles/components/ProblemModal.css"

const ProblemModal = ({ isOpen, onClose, problemData, isDarkMode }) => {
  const [votes, setVotes] = useState(problemData.votesCount || 0)
  const [hasVoted, setHasVoted] = useState(false)
  const { campaigns, loading: campaignsLoading, error: campaignsError } = useFetchCampaignsByProblem(problemData.id)
  const [selectedCampaign, setSelectedCampaign] = useState(null)

  // Format date to be more readable
  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  const handleVote = () => {
    if (!hasVoted) {
      setVotes(votes + 1)
      setHasVoted(true)
    }
  }

  // Handle opening the campaign modal
  const handleOpenCampaignModal = (campaign) => {
    setSelectedCampaign(campaign)
  }

  // Handle closing the campaign modal
  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null)
  }

  const darkModeClass = isDarkMode ? "dark-mode-modal" : ""

  return (
    <>
      <Modal
        show={isOpen}
        onHide={onClose}
        centered
        size="xl"
        dialogClassName="problem-modal-width"
        contentClassName={`${darkModeClass} modal-content-relative`}
      >
        <Modal.Header closeButton closeVariant={isDarkMode ? "white" : undefined} className="border-0 pb-0">
          <div className="modal-header-container">
            {/* Vote button - keep on the right */}
            <div className="modal-vote-button">
              <ThumbsUp size={20} className={`thumbs-up-icon ${hasVoted ? "voted" : ""}`} onClick={handleVote} />
              <span className="vote-count ms-1">{votes}</span>
            </div>

            {/* Title with centered urgent badge above it */}
            <div className="w-100 text-center">
              {/* Urgent badge - centered above title */}
              {problemData.isUrgent && !problemData.isSolved && (
                <div className="modal-urgent-badge-centered mb-2">
                  <AlertCircle size={16} className="me-1" />
                  <span>Urgent</span>
                </div>
              )}

              <h2 className="modal-title fw-bold">{problemData.title}</h2>
              {problemData.isSolved && (
                <div className="mt-2">
                  <Badge bg="success" className="badge-lg d-inline-flex align-items-center">
                    <CheckCircle size={16} className="me-1" />
                    Solved
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </Modal.Header>

        {/* Modal Body */}
        <Modal.Body>
          <div className="d-flex justify-content-center text-meta mb-4">
            <div className="d-flex align-items-center me-4">
              <Calendar className="me-2" size={16} />
              <span>Posted: {formatDate(problemData.createdOn)}</span>
            </div>
            <div className="d-flex align-items-center">
              <Users className="me-2" size={16} />
              <span>By: {problemData.createdBy}</span>
            </div>
          </div>

          <Row>
            <Col md={8}>
              <Card className={`mb-4 ${darkModeClass}`}>
                <Card.Body>
                  <h5 className="fw-bold text-center w-100 mb-3">DESCRIPTION</h5>
                  <p className="mb-0" style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}>
                    {problemData.description}
                  </p>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className={`mb-3 ${darkModeClass}`}>
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <MessageSquare size={18} className="me-2 text-primary" />
                    <span className="fw-bold">IDEAS</span>
                    <span className="ms-2 fs-4 fw-bold">{campaigns.length || problemData.associatedIdeasCount || 0}</span>
                  </div>

                  {/* Display associated ideas as clickable items */}
                  <div className="mt-3 ideas-list">
                    {campaignsLoading ? (
                      <div className="text-center py-3">
                        <Spinner animation="border" size="sm" />
                        <span className="ms-2">Loading ideas...</span>
                      </div>
                    ) : campaignsError ? (
                      <div className="text-danger">Error loading ideas</div>
                    ) : campaigns.length > 0 ? (
                      <div>
                        {campaigns.map((campaign) => (
                          <div 
                            key={campaign.id} 
                            className="idea-item mb-2 p-2 border-bottom clickable-idea"
                            onClick={() => handleOpenCampaignModal(campaign)}
                          >
                            <div className="d-flex align-items-start">
                              <Lightbulb size={16} className="me-2 mt-1 text-warning" />
                              <div className="flex-grow-1">
                                <div className="fw-bold d-flex justify-content-between align-items-center">
                                  <span>{campaign.title}</span>
                                  <ChevronRight size={16} className="idea-chevron" />
                                </div>
                                <div className="idea-proposal">{campaign.proposal.substring(0, 100)}...</div>
                                <div className="d-flex justify-content-between mt-1">
                                  <small className="text-muted">Votes: {campaign.voteUp || 0}</small>
                                  <small className="text-muted">{formatDate(campaign.createdOn)}</small>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-muted py-2">No ideas yet. Be the first to contribute!</div>
                    )}
                  </div>

                  {(problemData.location || problemData.category) && (
                    <div className="mt-3">
                      {problemData.location && (
                        <div className="mb-3">
                          <div className="d-flex align-items-center mb-2">
                            <MapPin size={16} className="me-2 text-primary" />
                            <span className="fw-bold">LOCATION</span>
                          </div>
                          <div className="ps-4">{problemData.location}</div>
                        </div>
                      )}

                      {problemData.category && (
                        <div>
                          <div className="d-flex align-items-center mb-2">
                            <Tag size={16} className="me-2 text-primary" />
                            <span className="fw-bold">CATEGORY</span>
                          </div>
                          <div className="ps-4">{problemData.category}</div>
                        </div>
                      )}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Modal.Body>

        {/* Modal Footer with Bootstrap Grid */}
        <Modal.Footer className="border-0 pt-0">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="flex-grow-1"></div>
            <div className="text-center mx-auto">
              <PostCampaign
                groupId={problemData.groupId}
                problemId={problemData.id}
                buttonText="Post an Idea"
                buttonVariant="success"
              />
            </div>
            <div className="flex-grow-1 text-end">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Render the CampaignModal when a campaign is selected */}
      {selectedCampaign && (
        <CampaignModal
          isOpen={!!selectedCampaign}
          onClose={handleCloseCampaignModal}
          campaignData={selectedCampaign}
        />
      )}
    </>
  )
}

// Map Redux state to component props
const mapStateToProps = (state) => ({
  isDarkMode: state.theme.isDarkMode,
})

// Connect component to Redux
export default connect(mapStateToProps)(ProblemModal)
